import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { Plus, Trash2 } from "lucide-react";

import { deleteTemplate, listTemplates } from "@/api/messaging";
import { ConfirmModal } from "@/components/ConfirmModal";
import { MenuItem } from "@/components/KebabMenu";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { ButtonLink, EmptyState, Page, PageHeader, StatusPill, Table, type TableColumn } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";
import { MESSAGING } from "@/lib/sections";
import type { MessagingTemplate } from "@/types/api";

function CategoryBadge({ category }: { category: MessagingTemplate["category"] }) {
  // A category is a label, not a state: no dot.
  return (
    <StatusPill tone={category === "service" ? "neutral" : "info"} dot={false}>
      {category === "service" ? "Service" : "News"}
    </StatusPill>
  );
}

const COLUMNS: TableColumn<MessagingTemplate>[] = [
  {
    key: "name",
    header: "Name",
    cell: (t) => (
      <>
        <Link to={`/messaging/templates/${encodeURIComponent(t.slug)}/edit`} className="text-brand-700 hover:underline">
          {t.name}
        </Link>
        <div className="font-mono text-xs text-ink-muted">{t.slug}</div>
      </>
    ),
  },
  { key: "category", header: "Category", cell: (t) => <CategoryBadge category={t.category} /> },
  { key: "subject", header: "Subject", cell: (t) => t.subject },
  {
    key: "updated",
    header: "Updated",
    mono: true,
    nowrap: true,
    cell: (t) => (
      <>
        {formatDateTime(t.updatedAt)} <span className="text-ink-muted">by {t.updatedBy}</span>
      </>
    ),
  },
];

export function MessagingTemplatesPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState<MessagingTemplate | null>(null);

  const templates = useQuery({
    queryKey: ["messaging", "templates"],
    queryFn: () => listTemplates(token),
    enabled: !!token,
  });

  const remove = useMutation({
    mutationFn: (slug: string) => deleteTemplate(token, slug),
    onSuccess: () => {
      setToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["messaging", "templates"] });
    },
  });

  return (
    <Page>
      <PageHeader
        section={MESSAGING}
        title="Templates"
        subtitle="Templates are written in Markdown with a fixed set of variables and sent in the PulseStation layout. A campaign keeps its own copy of the template it sent."
        actions={
          <ButtonLink to="/messaging/templates/new" variant="primary" icon={Plus}>
            New template
          </ButtonLink>
        }
      />

      <RefreshFailed query={templates} className="mb-4" />
      <Table
        label="Templates"
        columns={COLUMNS}
        rows={templates.data?.items}
        rowKey={(t) => t.slug}
        error={loadFailure(templates)}
        empty={
          <EmptyState
            title="No templates yet"
            action={
              <ButtonLink to="/messaging/templates/new" variant="primary" icon={Plus}>
                New template
              </ButtonLink>
            }
          >
            Create one to start a campaign.
          </EmptyState>
        }
        actions={{
          label: (t) => `Actions for ${t.slug}`,
          items: (t) => (
            <MenuItem danger icon={Trash2} onClick={() => setToDelete(t)}>
              Delete…
            </MenuItem>
          ),
        }}
      />

      {toDelete && (
        <ConfirmModal
          title={`Delete "${toDelete.name}"`}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          pending={remove.isPending}
          danger
          error={remove.error}
          onCancel={() => {
            // ``remove`` is shared by every row: without the reset, the next
            // template's modal opens on this one's error.
            remove.reset();
            setToDelete(null);
          }}
          onConfirm={() => remove.mutate(toDelete.slug)}
        >
          <p>
            Campaigns already sent keep their own copy of this template. Draft campaigns that
            reference it will need another template before they can be sent.
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
